import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";

// ── UI Taraflı Rate Limiting (Register) ──────────────────────────────────────
const RL_KEY_REG     = "register_attempts";
const MAX_REG        = 3;
const LOCKOUT_REG_MS = 30 * 60 * 1000;

function getRegRL() {
  try { return JSON.parse(localStorage.getItem(RL_KEY_REG)) || { count:0, lockedUntil:0 }; }
  catch { return { count:0, lockedUntil:0 }; }
}
function recordRegFail() {
  const d = getRegRL();
  const count = d.count + 1;
  const lockedUntil = count >= MAX_REG ? Date.now() + LOCKOUT_REG_MS : d.lockedUntil;
  localStorage.setItem(RL_KEY_REG, JSON.stringify({ count, lockedUntil }));
  return { count, lockedUntil };
}
function resetReg() { localStorage.setItem(RL_KEY_REG, JSON.stringify({ count:0, lockedUntil:0 })); }
function getRegRemaining() { return Math.max(0, getRegRL().lockedUntil - Date.now()); }

const rules = [
  { label:"En az 8 karakter",               test: p => p.length >= 8 },
  { label:"En az bir büyük harf (A–Z)",     test: p => /[A-Z]/.test(p) },
  { label:"En az bir küçük harf (a–z)",     test: p => /[a-z]/.test(p) },
  { label:"En az bir rakam (0–9)",           test: p => /[0-9]/.test(p) },
  { label:"En az bir özel karakter (!@#…)", test: p => /[!@#$%^&*()\-_=+[\]{};':",.<>/?\\|`~]/.test(p) },
];

export default function Register() {
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [emailSent, setEmailSent] = useState(false); // doğrulama maili gönderildi mi

  const [lockoutRemaining, setLockoutRemaining] = useState(getRegRemaining());
  const [attemptCount, setAttemptCount]         = useState(getRegRL().count);
  const timerRef = useRef(null);

  const { register } = useAuth();

  useEffect(() => {
    if (lockoutRemaining <= 0) return;
    timerRef.current = setInterval(() => {
      const rem = getRegRemaining();
      setLockoutRemaining(rem);
      if (rem <= 0) { clearInterval(timerRef.current); resetReg(); setAttemptCount(0); setError(""); }
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [lockoutRemaining]);

  const formatTime = (ms) => {
    const secs = Math.ceil(ms / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2,"0")}` : `${s}s`;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const rem = getRegRemaining();
    if (rem > 0) { setError(`Çok fazla deneme. ${formatTime(rem)} bekleyin.`); return; }
    if (password !== confirm) return setError("Şifreler eşleşmiyor.");
    if (!rules.every(r => r.test(password))) return setError("Şifre tüm güvenlik kurallarını karşılamıyor.");

    setLoading(true);
    try {
      await register(email, password, name);
      resetReg();
      setEmailSent(true); // doğrulama ekranını göster
    } catch (err) {
      const { count, lockedUntil } = recordRegFail();
      setAttemptCount(count);
      if (lockedUntil > Date.now()) {
        setLockoutRemaining(getRegRemaining());
        setError(`${MAX_REG} başarısız deneme. Kayıt ${formatTime(LOCKOUT_REG_MS)} süreyle engellendi.`);
        setLoading(false);
        return;
      }
      const messages = {
        "auth/email-already-in-use": "Bu e-posta zaten kullanımda.",
        "auth/invalid-email":        "Geçersiz e-posta adresi.",
        "auth/weak-password":        "Şifre çok zayıf.",
      };
      const base = messages[err.code] || "Kayıt olunamadı. Lütfen tekrar deneyin.";
      const left = MAX_REG - count;
      setError(left > 0 ? `${base} (${left} deneme hakkı kaldı)` : base);
    }
    setLoading(false);
  }

  // ── Doğrulama maili gönderildi ekranı ──────────────────────────────────────
  if (emailSent) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign:"center" }}>
          <div style={{ fontSize:"4rem", marginBottom:"1rem" }}>📬</div>
          <h1 style={{ marginBottom:"0.5rem" }}>E-postanı Doğrula</h1>
          <p style={{ color:"#94a3b8", lineHeight:"1.7", marginBottom:"1.5rem" }}>
            <strong style={{ color:"#e2e8f0" }}>{email}</strong> adresine bir doğrulama bağlantısı gönderdik.<br />
            Gelen kutunu (ve spam klasörünü) kontrol et, bağlantıya tıkladıktan sonra giriş yapabilirsin.
          </p>
          <div style={{ background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:"12px", padding:"0.85rem 1rem", marginBottom:"1.5rem", fontSize:"0.85rem", color:"#fde68a" }}>
            ⚠️ Bağlantı <strong>10 dakika</strong> geçerlidir. Mail gelmezse spam klasörünü kontrol et.
          </div>
          <Link to="/login">
            <button className="auth-btn">Giriş Sayfasına Git →</button>
          </Link>
        </div>
      </div>
    );
  }

  const isLocked = lockoutRemaining > 0;
  const allPassed = rules.every(r => r.test(password));

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">⬡</div>
          <h1>Hesap Oluştur</h1>
          <p>Birkaç adımda kayıt ol</p>
        </div>

        {isLocked && (
          <div className="auth-error" style={{ textAlign:"center",padding:"0.85rem 1rem" }}>
            <div style={{ fontSize:"1.5rem",marginBottom:"0.3rem" }}>🔒</div>
            <strong>Kayıt Geçici Olarak Engellendi</strong>
            <div style={{ marginTop:"0.35rem",fontSize:"0.85rem" }}>
              Kalan süre: <strong style={{ fontFamily:"monospace",color:"#fbbf24" }}>{formatTime(lockoutRemaining)}</strong>
            </div>
          </div>
        )}

        {!isLocked && attemptCount > 0 && (
          <div style={{ background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:"10px",padding:"0.65rem 1rem",marginBottom:"0.75rem",fontSize:"0.82rem",color:"#fde68a",textAlign:"center" }}>
            ⚠️ {MAX_REG - attemptCount} deneme hakkın kaldı
          </div>
        )}

        {error && !isLocked && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>Ad Soyad</label>
            <input type="text" placeholder="Adın Soyadın" value={name}
              onChange={e => setName(e.target.value)} required disabled={isLocked} />
          </div>
          <div className="form-group">
            <label>E-posta</label>
            <input type="email" placeholder="ornek@mail.com" value={email}
              onChange={e => setEmail(e.target.value)} required disabled={isLocked} />
          </div>
          <div className="form-group">
            <label>Şifre</label>
            <input type="password" placeholder="Güçlü bir şifre girin" value={password}
              onChange={e => setPassword(e.target.value)} required disabled={isLocked} />
          </div>

          {password.length > 0 && (
            <div className="password-rules">
              <p style={{ fontSize:"0.75rem",color:"#6b6b80",marginBottom:"6px",fontWeight:"500" }}>Şifre kuralları:</p>
              {rules.map((rule, i) => (
                <p key={i} style={{ fontSize:"0.73rem",color:rule.test(password)?"#4ade80":"#f87171",marginBottom:"2px" }}>
                  {rule.test(password) ? "✓" : "✗"} {rule.label}
                </p>
              ))}
            </div>
          )}

          <div className="form-group">
            <label>Şifre Tekrar</label>
            <input type="password" placeholder="Şifreni tekrar gir" value={confirm}
              onChange={e => setConfirm(e.target.value)} required disabled={isLocked} />
          </div>

          <button type="submit" className="auth-btn" disabled={loading || isLocked || !allPassed}>
            {loading ? <span className="spinner" /> : isLocked ? `🔒 ${formatTime(lockoutRemaining)}` : "Kayıt Ol"}
          </button>
        </form>

        <p className="auth-switch">
          Zaten hesabın var mı? <Link to="/login">Giriş yap</Link>
        </p>
      </div>
    </div>
  );
}
